/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe } from "@angular/common";
import { Component, inject, OnDestroy } from "@angular/core";
import { Observable, Subject, first, map, of, switchMap } from "rxjs";
import { DatetimePipe } from "typedb-cloud-framework";

import { ApplicationState } from "../../../service/application-state.service";
import { ResourceAvailability, PageScaffoldComponent } from "../../scaffold/page/page-scaffold.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, RouterLink, RouterOutlet } from "@angular/router";
import { MatSidenavModule } from "@angular/material/sidenav";
import { TeamApi } from "../../../service/team/team-api.service";
import { checkScrollLocationFromIDs } from "../../../util";

@Component({
    selector: "tp-team-settings-scaffold",
    templateUrl: "./team-settings-scaffold.component.html",
    styleUrls: ["./team-settings-scaffold.component.scss", "./settings.scss"],
    imports: [
        PageScaffoldComponent, AsyncPipe,
        DatetimePipe, MatTooltipModule, RouterLink, MatSidenavModule, RouterOutlet
    ]
})
export class TeamSettingsScaffoldComponent implements OnDestroy {
    readonly team = this.app.requireCurrentTeam();
    readonly unsub$ = new Subject<void>();
    readonly teamMemberCount$ = this.countTeamMembers();
    readonly availability$: Observable<ResourceAvailability> = this.app.hasTeamAdminAccess$.pipe(map(x => x ? "ready" : "loading"));

    route = inject(ActivatedRoute);
    get activeSettingsSection() {
        return this.route.snapshot.firstChild?.data["settingsSection"];
    }

    constructor(private app: ApplicationState, private teamApi: TeamApi) {
        this.route.fragment.pipe(
            switchMap(fragment => this.availability$.pipe(
                first(x => x === "ready"),
                map(_ => fragment),
            ))
        ).subscribe(fragment => {
            setTimeout(() => {
                const el = document.getElementById(fragment!);
                if (el) el.scrollIntoView(true);
            });
        });
    }

    ngOnDestroy() {
        this.unsub$.next();
    }

    generalScrollElementIDs = ["details", "members", "api-tokens", "danger-zone"];
    billingScrollElementIDs = ["upcoming-bill", "invoices", "card", "marketplace", "credits", "discounts"];
    currentActive = 'top';

    checkScrollLocation($event: any) {
        const scrollTop = $event.target.scrollTop + 78; // Account for height of header
        if (this.activeSettingsSection === "general") {
            this.currentActive = checkScrollLocationFromIDs(this.generalScrollElementIDs, scrollTop) ?? 'top';
        } else if (this.activeSettingsSection === "billing") {
            this.currentActive = checkScrollLocationFromIDs(this.billingScrollElementIDs, scrollTop) ?? 'top';
        }
    }

    countTeamMembers() {
        const pageSize = 1000;
        return this.countTeamMembersOnPage(0, pageSize);
    }

    countTeamMembersOnPage(offset: number, pageSize: number): Observable<number> {
        return this.teamApi.listMembers(
            this.team.uuid, { sorting: { attributeType: "id" }, pagination: { offset, limit: pageSize }}, of()
        ).pipe(
            switchMap(res => {
                const memberCount = res.initial!.length;
                if (memberCount === pageSize) return this.countTeamMembersOnPage(offset + pageSize, pageSize);
                else return of(memberCount);
            })
        );
    }
}
